/* eslint-disable react/prop-types */
/**
 * EmissionsRoutingPanel
 * =====================
 * Iter186 — Job Intelligence panel rendering the emissions reasoning
 * engine classification.
 *
 * Three visual states, one component:
 *
 *   BLOCKED_TEST       — amber "Blocking Condition" panel + staged
 *                        repair plan + suppressed-parts note.
 *   UNKNOWN_CAUSE      — blue "Root Cause Unknown" panel + recommend
 *                        diagnostic assessment.
 *   CONFIRMED_FAILURE  — emerald "Confirmed Failure" panel + replace
 *                        component + retest.
 *
 * Pulls everything from the props — no axios calls. The parent
 * (`App.js` FOH pricing screen) reads ``data?.emissions_routing`` from
 * GET ``/api/foh/yestimate/{id}`` and passes it through.
 */
import { AlertTriangle, Activity, ShieldCheck, ArrowRight } from "lucide-react";

const STATE_STYLES = {
  BLOCKED_TEST: {
    container:    "border-amber-300 bg-amber-50/70",
    iconWrap:     "bg-amber-100 text-amber-800",
    badge:        "bg-amber-700 text-white",
    stageWrap:    "border-amber-200 bg-white",
    Icon:         AlertTriangle,
  },
  UNKNOWN_CAUSE: {
    container:    "border-blue-300 bg-blue-50/70",
    iconWrap:     "bg-blue-100 text-blue-800",
    badge:        "bg-blue-700 text-white",
    stageWrap:    "border-blue-200 bg-white",
    Icon:         Activity,
  },
  CONFIRMED_FAILURE: {
    container:    "border-emerald-300 bg-emerald-50/70",
    iconWrap:     "bg-emerald-100 text-emerald-800",
    badge:        "bg-emerald-700 text-white",
    stageWrap:    "border-emerald-200 bg-white",
    Icon:         ShieldCheck,
  },
};

export default function EmissionsRoutingPanel({ routing }) {
  if (!routing) return null;
  const state = routing.emissions_state;
  if (!state || state === "NOT_APPLICABLE") return null;
  const styles = STATE_STYLES[state] || STATE_STYLES.UNKNOWN_CAUSE;
  const Icon = styles.Icon;
  const panel = routing.job_intelligence_panel || {};
  const stages = routing.staged_workflow || [];
  const suppressed = routing.suppressed_part_rules || [];

  return (
    <section
      id="emissions-routing-panel-anchor"
      className={`rounded-lg border-2 ${styles.container} px-4 py-3 mb-4`}
      data-testid="emissions-routing-panel"
      data-state={state}
    >
      <header className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${styles.iconWrap} shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-[14px] font-bold text-slate-900 leading-tight">
              {panel.title || "Emissions Routing"}
            </h3>
            {routing.badge && (
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${styles.badge}`}
                data-testid="emissions-routing-badge"
              >
                {routing.badge}
              </span>
            )}
          </div>
          {panel.summary && (
            <p className="text-[12.5px] text-slate-700 leading-snug mt-1">
              {panel.summary}
            </p>
          )}
          {routing.recommended_next_step && (
            <p className="text-[12.5px] text-slate-900 font-semibold mt-1.5">
              Recommended next step: {routing.recommended_next_step}
            </p>
          )}
        </div>
      </header>

      {/* Staged workflow */}
      {stages.length > 0 && (
        <ol
          className="mt-3 space-y-1.5"
          data-testid="emissions-routing-staged-workflow"
        >
          {stages.map((s) => (
            <li
              key={`stage-${s.stage}`}
              className={`rounded border ${styles.stageWrap} px-2.5 py-1.5 flex items-start gap-2 text-[12.5px]`}
              data-testid={`emissions-routing-stage-${s.stage}`}
            >
              <span className={`shrink-0 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${styles.badge}`}>
                {s.stage}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 leading-snug">
                  {s.action}
                </p>
                {s.condition && (
                  <p className="text-[11.5px] text-slate-600 italic leading-snug">
                    {s.condition}
                  </p>
                )}
                {s.rationale && (
                  <p className="text-[11.5px] text-slate-500 leading-snug mt-0.5">
                    {s.rationale}
                  </p>
                )}
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
            </li>
          ))}
        </ol>
      )}

      {/* Suppressed-parts disclosure */}
      {routing.suppress_part_recommendations && suppressed.length > 0 && (
        <div
          className="mt-3 text-[11.5px] text-slate-600 bg-white/70 border border-dashed border-slate-300 rounded px-2.5 py-1.5"
          data-testid="emissions-routing-suppressed-parts"
        >
          <span className="font-semibold uppercase tracking-wider text-[10px]">
            Auto-pricing suppressed
          </span>
          <span className="ml-1">
            — {suppressed.length} emissions parts rule(s) blocked until the
            classification changes. FOH can still add parts manually if
            evidence supports it.
          </span>
        </div>
      )}

      {/* Evidence count */}
      {routing.linked_evidence && routing.linked_evidence.length > 0 && (
        <div
          className="mt-2 text-[11px] text-slate-500"
          data-testid="emissions-routing-evidence-count"
        >
          Based on {routing.linked_evidence.length} linked piece(s) of evidence.
        </div>
      )}
    </section>
  );
}
